import { Check, Plus } from 'lucide-react';
import { formatCurrency } from '../../utils';
import type { EquipmentOption, EquipmentServices } from '../../types';

interface EquipmentOptionsProps {
  options: EquipmentOption[];
  services?: EquipmentServices;
  fullWidth?: boolean;
}

export function EquipmentOptions({ options, services, fullWidth = false }: EquipmentOptionsProps) {
  const hasOptions = options && options.length > 0;
  const hasServices = services && (services.pmContract || services.gps?.added);

  if (!hasOptions && !hasServices) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Options */}
      {hasOptions && (
        <div>
          <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
            <Plus className="w-3 h-3" />
            Included Options
          </h4>
          <ul className={fullWidth ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" : "space-y-2"}>
            {options.map((option, index) => (
              <li
                key={index}
                className="flex items-center gap-2 py-2 px-3 bg-green-50 rounded-lg border border-green-100"
              >
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="flex-1 min-w-0 text-sm truncate">{option.name}</span>
                {option.price > 0 && (
                  <span className="text-sm font-mono text-green-700 flex-shrink-0 whitespace-nowrap">
                    +{formatCurrency(option.price)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Per-Equipment Services */}
      {hasServices && (
        <div>
          <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
            <Check className="w-3 h-3" />
            Equipment Services
          </h4>
          <ul className="space-y-2">
            {services.pmContract && (
              <li className="flex items-center justify-between py-2 px-3 bg-blue-50 rounded-lg border border-blue-100">
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span>
                    PM
                    <span className="text-gray-500 ml-1">
                      (Every {services.pmContract.frequency}{!String(services.pmContract.frequency).toLowerCase().includes('day') ? ' days' : ''})
                    </span>
                  </span>
                </span>
                <span className="text-sm font-mono text-blue-700">
                  {formatCurrency(services.pmContract.price)}/service
                </span>
              </li>
            )}
            {services.gps?.added && (
              <li className="flex items-center justify-between py-2 px-3 bg-purple-50 rounded-lg border border-purple-100">
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 bg-purple-500 rounded-full" />
                  GPS Fleet Tracking
                </span>
                <span className="text-sm font-mono text-purple-700">
                  {formatCurrency(services.gps.price)}
                </span>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// Summary of options total
interface OptionsSummaryProps {
  options: EquipmentOption[];
  quantity: number;
}

export function OptionsSummary({ options, quantity }: OptionsSummaryProps) {
  if (!options || options.length === 0) return null;

  const total = options.reduce((sum, opt) => sum + opt.price, 0) * quantity;

  return (
    <div className="text-sm text-gray-600">
      <span className="font-medium">{options.length}</span> options included
      <span className="mx-2">·</span>
      <span className="font-mono text-hul-navy">+{formatCurrency(total)}</span>
    </div>
  );
}
